import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { ReploIntegration } from "schemas/generated/integration";

import React from "react";

import { useKeyboardListNavigation } from "@agent-ui/features/chat/components/chat-elements/context-items-menu/useKeyboardListNavigation";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ALL_CATEGORIZED_INTEGRATIONS } from "@v2/features/integrations/integrationCategories";
import IntegrationStatusTag from "@v2/features/integrations/IntegrationStatusTag";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import { Search } from "lucide-react";

const IntegrationContextItemSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-3 rounded-xl border-0.5 border-border p-3 min-h-[100px]">
      <div className="flex items-start justify-between">
        <Skeleton className="h-7 w-7 rounded-lg" />
        <Skeleton className="h-4 w-16" />
      </div>
      <div className="flex flex-col gap-0.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-40" />
      </div>
    </div>
  );
};

const IntegrationContextItem = React.forwardRef<
  HTMLButtonElement,
  {
    integrationType: IntegrationType;
    isConnected: boolean;
    onIntegrationSelect: (integrationType: IntegrationType) => void;
    isFocused?: boolean;
  }
>(
  (
    { integrationType, isConnected, onIntegrationSelect, isFocused = false },
    ref,
  ) => {
    const config = INTEGRATION_CONFIGS[integrationType];

    return (
      <button
        ref={ref}
        key={integrationType}
        className={twMerge(
          "group relative flex flex-col gap-3 rounded-xl border-0.5 border-border p-3 transition-colors min-h-[100px] hover:bg-button-secondary-hover cursor-pointer text-left",
          "focus:outline-none focus:ring-2 focus:ring-primary/50",
          isFocused && "bg-menu-item-selected",
        )}
        onClick={() => {
          onIntegrationSelect(integrationType);
        }}
      >
        <div className="flex items-start justify-between">
          <img
            src={config.image}
            alt={config.title}
            className="size-7 rounded-lg object-contain"
          />
          <IntegrationStatusTag
            status={isConnected ? "connected" : "not_connected"}
          />
        </div>

        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-default break-words">
            {config.title}
          </span>
          <span className="text-xs text-muted break-words leading-relaxed line-clamp-2">
            {config.description}
          </span>
        </div>
      </button>
    );
  },
);

IntegrationContextItem.displayName = "IntegrationContextItem";

interface IntegrationContextItemsListProps {
  integrations: ReploIntegration[];
  isLoading: boolean;
  onIntegrationSelect: (integrationType: IntegrationType) => void;
  searchQuery: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

export const IntegrationContextItemsList: React.FC<
  IntegrationContextItemsListProps
> = ({
  integrations,
  isLoading,
  searchQuery,
  onIntegrationSelect,
  onKeyDown,
}) => {
  const integrationsByType = React.useMemo(() => {
    const map = new Map<IntegrationType, ReploIntegration>();
    for (const integration of integrations) {
      if (
        integration.type !== "ShopifyIntegrationConfig" &&
        integration.type !== "StripeConnect"
      ) {
        map.set(integration.type, integration);
      }
    }
    return map;
  }, [integrations]);

  const filteredIntegrations = React.useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase();
    return ALL_CATEGORIZED_INTEGRATIONS.filter((type) => {
      const config = INTEGRATION_CONFIGS[type];
      return (
        config.title.toLowerCase().includes(lowerQuery) ||
        config.description.toLowerCase().includes(lowerQuery)
      );
    });
  }, [searchQuery]);

  const sortedIntegrations = React.useMemo(() => {
    const connected: IntegrationType[] = [];
    const notConnected: IntegrationType[] = [];

    for (const type of filteredIntegrations) {
      if (integrationsByType.has(type)) {
        connected.push(type);
      } else {
        notConnected.push(type);
      }
    }

    return [...connected, ...notConnected];
  }, [filteredIntegrations, integrationsByType]);

  const { focusedIndex, handleKeyDown, getItemRef } = useKeyboardListNavigation(
    {
      itemCount: sortedIntegrations.length,
      onSelect: (index) => {
        if (sortedIntegrations[index]) {
          onIntegrationSelect(sortedIntegrations[index]);
        }
      },
      isEnabled: !isLoading && sortedIntegrations.length > 0,
    },
  );

  const noIntegrations =
    sortedIntegrations.length === 0 && !isLoading && searchQuery.length === 0;
  const emptySearch =
    sortedIntegrations.length === 0 && !isLoading && searchQuery.length > 0;

  const combinedKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      handleKeyDown(event);
      onKeyDown?.(event);
    },
    [handleKeyDown, onKeyDown],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col flex-1">
        <div className="flex-1 overflow-y-auto p-2">
          <div className="flex flex-col gap-1">
            {Array.from({ length: 10 }).map((_, index) => (
              <IntegrationContextItemSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (noIntegrations) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden h-full pb-3">
        <div className="flex-1 p-2 flex items-center justify-center">
          <Empty
            title="No Integrations Available"
            description="Integrations will appear here when available."
            size="sm"
            icon={<Search />}
          />
        </div>
      </div>
    );
  }

  if (emptySearch) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden h-full pb-3">
        <div className="flex-1 p-2 flex items-center justify-center">
          <Empty
            title="No Integrations Found"
            description="Try adjusting your search."
            size="sm"
            icon={<Search />}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col flex-1"
      onKeyDown={combinedKeyDown}
      tabIndex={0}
      data-list-container="integrations"
    >
      <div className="flex flex-col gap-2 p-2 pb-1">
        {sortedIntegrations.map((integrationType, index) => {
          const isConnected = integrationsByType.has(integrationType);
          return (
            <IntegrationContextItem
              key={integrationType}
              ref={getItemRef(index)}
              integrationType={integrationType}
              isConnected={isConnected}
              onIntegrationSelect={onIntegrationSelect}
              isFocused={index === focusedIndex}
            />
          );
        })}
      </div>
    </div>
  );
};
